import api from "@/assets/js/utils/api.js";
import { useHead } from "@vueuse/head";
import {
  showCustom,
  showSuccess,
  showLoading,
  hideLoading,
} from "@/assets/js/utils/alertService.js";

export default {
  name: "ProductsWithCategoryNav",

  setup() {
    useHead({
      title: "商品專區｜安裕和國際企業社",
      meta: [
        {
          name: "description",
          content:
            "安裕和國際企業社商品專區，提供動物醫療設備、獸醫耗材、臨床專用產品。精選高品質醫材，符合動物醫院日常臨床需求。",
        },
        {
          name: "keywords",
          content:
            "動物醫療, 醫療設備, 動物醫院耗材, 獸醫用品, 臨床器材, 動物醫療採購",
        },

        // 🟦 Open Graph（Facebook / LINE 貼文圖片預覽）
        { property: "og:title", content: "安裕和國際企業社｜商品專區" },
        {
          property: "og:description",
          content:
            "臨床嚴選動物醫療設備與醫療耗材，適用全台動物醫院與獸醫診所。",
        },
        {
          property: "og:image",
          content: "https://www.annyoohoh.com.tw/img/AYH.f5146a33.png",
        },
        { property: "og:type", content: "website" },
        {
          property: "og:url",
          content: "https://www.annyoohoh.com.tw/products",
        },
      ],

      // 🟧 Canonical：避免 Google 當重複網頁
      link: [
        {
          rel: "canonical",
          href: "https://www.annyoohoh.com.tw/products",
        },
      ],
    });
  },

  data() {
    return {
      title: "商品專區",

      // 分類數據
      categories: [],
      subToMainMap: new Map(), // 子分類ID -> 主分類ID  // 控制分類對照
      mainNameMap: new Map(), // 主分類ID -> 主分類名稱 // 控制名稱對照
      subNameMap: new Map(), // 子分類ID -> 子分類名稱 // 控制名稱對照

      // 商品數據
      products: [],

      // 選擇狀態
      selectedMainCategory: "all",
      selectedSubCategory: null,
      selectedCategory: 0, // 保留原有屬性相容性

      // 分頁相關
      currentPage: 1,
      itemsPerPage: 6,

      // UI 狀態
      showDropdown: false,
      showModal: false,
      selectedProduct: {},
      showBackToTop: false,
      isCartAnimating: false,
      cartItemCount: 0,
      showLeaseModal: false,

      selectedVariantIndex: 0, // ← 預設第一個規格
      buyQuantity: 1, // ← 預設購買 1

      searchQuery: "", // 搜尋關鍵字
    };
  },

  computed: {
    // 當前選中主分類的子分類
    currentSubCategories() {
      if (this.selectedMainCategory === "all") return [];
      const category = this.categories.find(
        (c) => c.id === this.selectedMainCategory,
      );
      return category ? category.subcategories : [];
    },

    // 篩選後的商品
    filteredProducts() {
      // 🟢 Step 1：以全部商品為基礎（避免動到原始 products）
      let filtered = [...this.products]; // 控制：商品資料來源

      // 🟢 Step 2：只搜尋「商品名稱 name」
      if (this.searchQuery && this.searchQuery.trim() !== "") {
        const query = this.searchQuery.toLowerCase().trim(); // 控制：搜尋關鍵字

        filtered = filtered.filter(
          (p) => p.name.toLowerCase().includes(query), // 只比對標題
        );
      }

      // 🟢 Step 3：主分類過濾（不是全部商品才過濾）
      if (this.selectedMainCategory !== "all") {
        filtered = filtered.filter(
          (p) => p.category === this.selectedMainCategory, // 控制：主分類
        );
      }

      // 🟢 Step 4：子分類過濾
      if (this.selectedSubCategory) {
        filtered = filtered.filter(
          (p) => p.subCategoryId === this.selectedSubCategory, // 控制：子分類
        );
      }

      // 🟢 Step 5：回傳最終結果
      return filtered;
    },

    // ✅ 分頁邏輯
    paginatedProducts() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredProducts.slice(start, start + this.itemsPerPage);
    },

    totalPages() {
      return Math.ceil(this.filteredProducts.length / this.itemsPerPage);
    },

    // 顯示的頁碼
    displayedPages() {
      const pages = [];
      const maxVisiblePages = 5;
      const halfVisible = Math.floor(maxVisiblePages / 2);

      let start = Math.max(1, this.currentPage - halfVisible);
      let end = Math.min(this.totalPages, start + maxVisiblePages - 1);

      if (end - start < maxVisiblePages - 1) {
        start = Math.max(1, end - maxVisiblePages + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    },

    // 保留原有的排序分類計算屬性
    sortedCategories() {
      return this.categories;
    },

    // 🟦 当前選中的規格資料
    currentVariant() {
      if (!this.selectedProduct.hasVariant) return null;
      return this.selectedProduct.variants[this.selectedVariantIndex] || null;
    },

    // 🟦 动態顯示價格給 Modal
    displayPrice() {
      // 單規格商品 → 用母體價格
      if (!this.selectedProduct.hasVariant) {
        return this.selectedProduct.price;
      }
      // 多規格 → 用規格價格
      const v = this.currentVariant;
      if (!v) return this.selectedProduct.price;
      return `NT$ ${Number(v.Price).toLocaleString()}`;
    },
  },

  methods: {
    async loadCategories() {
      try {
        const res = await api.get("/category/all");
        if (res.data?.success && Array.isArray(res.data.data)) {
          // 清空 map
          this.subToMainMap = new Map();
          this.mainNameMap = new Map();
          this.subNameMap = new Map();

          const activeMain = res.data.data
            .filter((m) => m.IsActive)
            .map((m) => {
              this.mainNameMap.set(m.MainCategoryId, m.MainCategoryName);
              const subs = (m.SubCategories || [])
                .filter((s) => s.IsActive)
                .map((s) => {
                  this.subToMainMap.set(s.SubCategoryId, m.MainCategoryId);
                  this.subNameMap.set(s.SubCategoryId, s.SubCategoryName);
                  return { id: s.SubCategoryId, name: s.SubCategoryName };
                });
              return {
                id: m.MainCategoryId,
                name: m.MainCategoryName,
                subcategories: subs,
              };
            });

          this.categories = [
            { id: "all", name: "全部商品", subcategories: [] },
            ...activeMain,
          ];
        } else {
          console.warn("分類資料格式錯誤：", res.data);
        }
      } catch (err) {
        console.error("無法取得分類資料：", err);
      }
    },
    async loadProducts() {
      try {
        const res = await api.get("/product/list");

        if (res.data?.success && Array.isArray(res.data.data)) {
          this.products = res.data.data
            .filter((p) => p.IsActive) // 🟢 僅顯示啟用商品
            .map((p) => {
              // 🟦 寫一個 helper 把 variants 轉成前端需要的格式
              let variants = [];

              if (p.HasVariant === true && Array.isArray(p.Variants)) {
                variants = p.Variants.map((v) => ({
                  id: v.Id, // 規格ID
                  sku: v.Sku || "", // SKU
                  VariantName: v.VariantName, // 規格名稱
                  Price: v.Price, // 售價
                  Stock: v.Stock, // 庫存
                  Meter: v.Meter ? 1 : 0, // 是否用公尺計價
                }));
              }

              return {
                id: p.Id, // 商品ID
                name: p.ProductName, // 商品名稱
                description: p.Description || "", // 商品描述

                // 🟢 單規格顯示的預設價格
                price: `NT$ ${Number(p.Price).toLocaleString()}`,

                // 🟦 判斷是否有多規格
                hasVariant: p.HasVariant === true,

                // 🟦 包含所有規格資料
                variants: variants,

                // 🟢 單規格商品的庫存 & 單位
                meter: p.Meter ? 1 : 0,
                stock: p.Stock,

                // 🟢 分類資料
                category: p.MainCategoryId,
                categoryName: p.MainCategoryName,
                subCategoryId: p.SubCategoryId,
                subCategoryName: p.SubCategoryName,

                // 🟢 樣式背景（你原本的）
                gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",

                // 🟢 圖片
                imageUrl:
                  p.PictureURL && p.PictureURL.trim() !== ""
                    ? p.PictureURL
                    : require("@/assets/image/AYH.webp"),

                // 🟢 影片
                videoUrl: p.VideoURL,

                // 🟢 Tags
                Tag1: p.Tag1 || "",
                Tag2: p.Tag2 || "",
                Tag3: p.Tag3 || "",
              };
            });
        }
      } catch (err) {
        console.error("❌ 無法取得商品資料：", err);
      }
    },

    handleImageError(event, product) {
      // 控制圖片載入錯誤
      console.warn(`圖片載入失敗：${product.name}`);
      event.target.src = require("@/assets/image/AYH.webp"); // 改用你的LOGO或預設圖
      event.target.onerror = null; // 避免無限觸發
    },

    async loadCartCount() {
      try {
        const res = await api.get("/front/cart");
        if (res.data?.success && res.data.data) {
          this.cartItemCount = res.data.data.ItemCount; // 後端的 ItemCount
        } else {
          this.cartItemCount = 0;
        }
      } catch (error) {
        console.warn("取得購物車數量失敗:", error);
        this.cartItemCount = 0;
      }
    },
    getCategoryBySubId(subId) {
      for (const main of this.categories) {
        if (main.subcategories.some((s) => s.id === subId)) {
          return main.id;
        }
      }
      return null;
    },

    // ✅ 主分類點擊
    selectMainCategory(categoryId) {
      this.selectedMainCategory = categoryId;
      this.selectedSubCategory = null;
      this.currentPage = 1;
    },

    // ✅ 子分類點擊
    selectSubCategory(subCategoryId) {
      this.selectedSubCategory =
        this.selectedSubCategory === subCategoryId ? null : subCategoryId;
      this.currentPage = 1;
    },

    // 重置篩選
    resetFilter() {
      this.selectedMainCategory = "all";
      this.selectedSubCategory = null;
      this.selectedCategory = 0;
      this.currentPage = 1;
    },

    // 獲取分類名稱
    getCategoryName(mainCategoryId) {
      if (mainCategoryId === "all") return "全部商品";
      return this.mainNameMap.get(mainCategoryId) || "";
    },

    // 獲取當前分類名稱 (用於手機版下拉選單)
    getCurrentCategoryName() {
      const category = this.categories.find(
        (c) => c.id === this.selectedMainCategory,
      );
      return category ? category.name : "全部商品";
    },

    // 切換下拉選單
    toggleDropdown() {
      this.showDropdown = !this.showDropdown;
    },

    // 選擇分類 (手機版相容)
    selectCategory(categoryId) {
      this.selectMainCategory(categoryId);
      this.showDropdown = false;
    },

    // 篩選商品 (保留原有方法)
    filterProducts(categoryId) {
      this.selectMainCategory(categoryId);
    },

    // 截取描述文字
    truncateDescription(description, maxLength = 50) {
      if (!description) return ""; // ✅ description 為 null、undefined 或空字串時直接回傳空字串
      return description.length > maxLength
        ? description.substring(0, maxLength) + "..."
        : description;
    },

    // 開啟商品模態框
    openProductModal(product) {
      this.selectedProduct = product;
      this.selectedVariantIndex = 0;
      this.buyQuantity = 1; // ← 每次開商品重置為 1
      this.showModal = true;
      document.body.style.overflow = "hidden";
    },

    // 關閉商品模態框
    closeProductModal() {
      this.showModal = false;
      document.body.style.overflow = "auto";
    },

    // 加入購物車
    async addToCart(product) {
      try {
        // 1️⃣ 檢查登入狀態
        const res = await api.get("/front/customer/verify");
        if (!res.data.success) {
          await showCustom("請先登入", "登入後才能將商品加入購物車", "warning");
          this.$router.push("/login");
          return;
        }

        // 2️⃣ 取使用者在 Modal 選的數量
        const qty = this.buyQuantity; // ← 直接用 Modal 的值

        // 🟦 防呆：數量不可為 0
        if (qty <= 0) {
          showCustom("錯誤", "請選擇購買數量", "warning");
          return;
        }

        // 🟦 防呆：檢查庫存
        const stock = this.currentVariant
          ? this.currentVariant.Stock
          : product.stock;

        // 🟦 Meter=1 才限制庫存
        if (product.meter === 1) {
          if (qty > stock) {
            showCustom("錯誤", `庫存不足，最多可購買 ${stock} 件`, "warning");
            return;
          }
        }

        // 3️⃣ 顯示 Loading
        showLoading("正在加入購物車...");

        // 4️⃣ 呼叫後端 API
        const addRes = await api.post("/front/cart/add", {
          ProductId: product.id,
          Qty: qty,
          VariantId: product.hasVariant
            ? product.variants[this.selectedVariantIndex].id
            : null,
        });

        // 5️⃣ 結果判斷
        hideLoading();
        if (addRes.data?.success) {
          await this.loadCartCount(); // 更新購物車數量

          // 🟩 加入成功動畫
          this.isCartAnimating = true;
          setTimeout(() => {
            this.isCartAnimating = false;
          }, 600);

          // 🟦 提示成功
          showSuccess(`已將 ${qty} 件 ${product.name} 加入購物車`);

          // 🟦 關閉 Modal
          this.closeProductModal();
        } else {
          showCustom("加入失敗", addRes.data?.message || "請稍後再試", "error");
        }
      } catch (error) {
        hideLoading();
        if (error.response?.status === 401) {
          await showCustom("請先登入", "登入後才能將商品加入購物車", "warning");
          this.$router.push("/login");
        } else {
          showCustom(
            "系統錯誤",
            error.response?.data?.message || "無法加入購物車，請稍後再試",
            "error",
          );
        }
      }
    },

    // 取得購物車中特定商品的當前數量
    async getCurrentProductQty(productId) {
      try {
        const res = await api.get("/front/cart");
        if (res.data?.success && res.data.data?.Details) {
          const item = res.data.data.Details.find(
            (item) => item.ProductId === productId,
          );
          return item ? item.Quantity : 0;
        }
        return 0;
      } catch (error) {
        console.warn("取得商品數量失敗:", error);
        return 0;
      }
    },
    // 前往購物車
    goToCart() {
      // 使用 Vue Router 導向 /cart
      this.$router.push("/cart"); // ✅ 導向購物車頁面
    },

    // 分頁方法
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.scrollToTop();
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.scrollToTop();
      }
    },

    changePage(page) {
      this.currentPage = page;
      this.scrollToTop();
    },

    // 回到頂部
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: "smooth" });
    },

    // 處理滾動事件
    handleScroll() {
      this.showBackToTop = window.scrollY > 300;
    },

    // 關閉租賃模態框
    closeLeaseModal() {
      this.showLeaseModal = false;
    },

    // 前往聯絡頁面
    goToContact() {
      // 聯絡頁面導航邏輯
      console.log("前往聯絡頁面");
    },

    selectVariant(index) {
      this.selectedVariantIndex = index; // 設定當前規格
      this.buyQuantity = 1; // 重設數量
    },

    // 🟦 減少數量
    decreaseQty() {
      if (this.buyQuantity > 1) {
        this.buyQuantity -= 1; // 數量 -1
      }
    },

    // 🟦 增加數量
    increaseQty() {
      // 🟦 meter = 0 → 自由增加不限制
      if (this.selectedProduct.meter === 0) {
        this.buyQuantity += 1;
        return;
      }

      // 🟦 meter = 1 → 依照庫存限制
      const max = this.currentVariant
        ? this.currentVariant.Stock
        : this.selectedProduct.stock;

      if (this.buyQuantity < max) {
        this.buyQuantity += 1;
      }
    },
    // 🟦 手動輸入安全保護
    validateQty() {
      if (this.buyQuantity < 1) this.buyQuantity = 1;

      // meter = 0 → 不限制最大值
      if (this.selectedProduct.meter === 0) return;

      // meter = 1 → 限制庫存
      const max = this.currentVariant
        ? this.currentVariant.Stock
        : this.selectedProduct.stock;

      if (this.buyQuantity > max) this.buyQuantity = max;
    },

    formatDescription(text) {
      if (!text) return "";

      return (
        text
          // 6Fr / 8Fr / 10Fr 前面換行
          .replace(/(\\s*)(\\d+Fr)/g, "<br>$2")
          // 第一行不要 <br>
          .replace(/^<br>/, "")
      );
    },

    goToProductDetail(productId) {
      this.$router.push(`/productitem/${productId}`);
    },

    // 🟢 處理搜尋
    handleSearch() {
      this.currentPage = 1; // 搜尋時回到第一頁
    },

    // 🟢 清除搜尋
    clearSearch() {
      this.searchQuery = "";
      this.currentPage = 1;
    },

    // 🟢 從 URL query 參數應用分類過濾
    applyQueryParams(query = this.$route.query) {
      const mainId = query.main ? parseInt(query.main) : null;
      const subId = query.sub ? parseInt(query.sub) : null;

      if (mainId) {
        this.selectedMainCategory = mainId;
        if (subId) {
          this.selectedSubCategory = subId;
          // ✅ 刪除了
        } else {
          this.selectedSubCategory = null;
          // ✅ 刪除了
        }
      } else {
        this.selectedMainCategory = "all";
        this.selectedSubCategory = null;
        // ✅ 刪除了
      }

      // ✅ title 永遠是 "商品專區"，不再修改

      this.currentPage = 1;
      this.scrollToTop();
    },
  },

  watch: {
    // 監聽路由 query 變化，更新分類選擇
    "$route.query": {
      handler(newQuery) {
        this.applyQueryParams(newQuery);
      },
      immediate: false, // 不要在初始化時立即執行，避免與 mounted 衝突
    },
  },

  async mounted() {
    // 🕐 設定超時保護（8秒，因為商品頁資料較多）
    const timeoutId = setTimeout(() => {
      if (typeof document !== "undefined") {
        console.log("商品頁載入超時，強制觸發預渲染事件");
        document.dispatchEvent(new Event("render-event"));
      }
    }, 8000);

    try {
      window.addEventListener("scroll", this.handleScroll);

      await this.loadCategories();
      await this.$nextTick();
      await this.loadProducts();
      await this.loadCartCount();

      // 🟢 載入完分類和商品後，應用 URL 參數
      this.applyQueryParams();

      const productId = this.$route.params.id;
      if (productId) {
        const target = this.products.find((p) => p.id == productId);
        if (target) {
          this.openProductModal(target);
          this.scrollToTop();
        }
      }

      document.addEventListener("click", (e) => {
        if (!e.target.closest(".category-dropdown-wrapper")) {
          this.showDropdown = false;
        }
      });

      // ✅ 檢查關鍵資料是否載入完成
      this.$nextTick(() => {
        const hasCategories = this.categories && this.categories.length > 0;
        const hasProducts = this.products && this.products.length > 0;

        if (hasCategories && hasProducts) {
          clearTimeout(timeoutId);
          console.log(
            `商品頁載入完成: ${this.categories.length} 個分類, ${this.products.length} 個商品`,
          );
        }

        if (typeof document !== "undefined") {
          document.dispatchEvent(new Event("render-event"));
        }
      });
    } catch (error) {
      console.error("商品頁面載入失敗:", error);
      clearTimeout(timeoutId);

      this.$nextTick(() => {
        if (typeof document !== "undefined") {
          document.dispatchEvent(new Event("render-event"));
        }
      });
    }
  },

  beforeUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
  },
};
